import { addDays, fromWallClock, hhmm, parseHHMM, shortDate, wallClock } from './time.js';

/*
 * Collection times. Placeholders until Saif confirms opening hours and slot
 * rules (TODO-SAIF). Everything here is a setting stored per site.
 */

/** 'HH:MM' open/close per weekday (0 = Sunday). null = closed. */
export type OpeningHours = Record<number, { open: string; close: string } | null>;

export const SEED_OPENING_HOURS: OpeningHours = {
  0: null, // closed on Sundays (CLAUDE.md §2)
  1: { open: '11:00', close: '20:00' },
  2: { open: '11:00', close: '20:00' },
  3: { open: '11:00', close: '20:00' },
  4: { open: '11:00', close: '20:00' },
  5: { open: '11:00', close: '20:00' },
  6: { open: '11:00', close: '20:00' },
};

export interface PickupRules {
  /** "As soon as possible" is ready about this many minutes after ordering. */
  asapMinutes: number;
  /** Scheduled slots are this many minutes apart. */
  slotMinutes: number;
  /** The first scheduled slot today is at least this far away. */
  minLeadMinutes: number;
  /** Last collection this many minutes before closing. */
  lastSlotBeforeCloseMinutes: number;
  /** How many days ahead a customer can order (1 = today and tomorrow). */
  daysAhead: number;
  /** Max slots shown for today (keeps the sheet short). */
  maxTodaySlots: number;
}

export const SEED_PICKUP_RULES: PickupRules = { asapMinutes: 12, slotMinutes: 15, minLeadMinutes: 15, lastSlotBeforeCloseMinutes: 0, daysAhead: 1, maxTodaySlots: 12 };

export interface PickupDay {
  dateKey: string;
  label: 'Today' | 'Tomorrow' | string;
  open: boolean;
  /** Only today, only while open: when an ASAP order would be ready. */
  asapReadyAt?: string;
  /** ISO instants of scheduled slots. */
  slots: string[];
}

export type PickupChoice = { type: 'asap' } | { type: 'slot'; at: string };

function slotTimes(dateKey: string, hours: { open: string; close: string }, rules: PickupRules, fromMinutes: number): Date[] {
  const openM = parseHHMM(hours.open);
  const closeM = parseHHMM(hours.close) - rules.lastSlotBeforeCloseMinutes;
  const out: Date[] = [];
  let m = Math.max(openM, fromMinutes);
  m = Math.ceil(m / rules.slotMinutes) * rules.slotMinutes;
  for (; m <= closeM; m += rules.slotMinutes) out.push(fromWallClock(dateKey, Math.floor(m / 60), m % 60));
  return out;
}

/**
 * What the customer can choose right now: today and `daysAhead` more days. When ordering ahead is allowed but none of those
 * days is open (Saturday evening, with Sunday closed), the list runs on to the next open day, so there is always a time to
 * pick. Days after tomorrow are labelled like "Mon 19 Oct".
 */
export function pickupDays(now: Date, hours: OpeningHours, rules: PickupRules): PickupDay[] {
  const w = wallClock(now);
  const days: PickupDay[] = [];
  for (let i = 0; i <= Math.max(rules.daysAhead, 7); i++) {
    if (i > rules.daysAhead && (rules.daysAhead === 0 || days.some((d, j) => j > 0 && d.slots.length > 0))) break;
    const dateKey = addDays(w.dateKey, i);
    const weekday = (w.weekday + i) % 7;
    const h = hours[weekday] ?? null;
    const label = i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : shortDate(fromWallClock(dateKey, 12, 0));
    if (!h) {
      days.push({ dateKey, label, open: false, slots: [] });
      continue;
    }
    if (i === 0) {
      const openM = parseHHMM(h.open), closeM = parseHHMM(h.close);
      const isOpen = w.minutes >= openM && w.minutes + rules.asapMinutes <= closeM;
      const slots = slotTimes(dateKey, h, rules, w.minutes + rules.minLeadMinutes).slice(0, rules.maxTodaySlots);
      const day: PickupDay = { dateKey, label, open: isOpen || slots.length > 0, slots: slots.map((d) => d.toISOString()) };
      if (isOpen) day.asapReadyAt = new Date(now.getTime() + rules.asapMinutes * 60000).toISOString();
      days.push(day);
    } else {
      days.push({ dateKey, label, open: true, slots: slotTimes(dateKey, h, rules, 0).map((d) => d.toISOString()) });
    }
  }
  return days;
}

export class PickupError extends Error {
  override name = 'PickupError';
}

/** Can this choice still be had? (The app asks for a time before paying when it can't; the server checks again.) */
export function pickupAvailable(choice: PickupChoice, days: PickupDay[]): boolean {
  return choice.type === 'asap' ? !!days[0]?.asapReadyAt : days.some((d) => d.slots.includes(new Date(choice.at).toISOString()));
}

/** Server-side check of the customer's choice. Returns when the order should be ready. */
export function validatePickup(now: Date, hours: OpeningHours, rules: PickupRules, choice: PickupChoice): { readyAt: Date; scheduled: boolean } {
  const days = pickupDays(now, hours, rules);
  if (choice.type === 'asap') {
    const today = days[0];
    if (!today?.asapReadyAt) throw new PickupError("We're closed right now. Pick a time instead.");
    return { readyAt: new Date(today.asapReadyAt), scheduled: false };
  }
  const at = new Date(choice.at);
  if (Number.isNaN(at.getTime())) throw new PickupError('Pick a collection time');
  // Allow a slot that was valid a minute ago (the customer may have been on the page a while).
  const graceNow = new Date(now.getTime() - 2 * 60000);
  const valid = pickupDays(graceNow, hours, rules).some((d) => d.slots.includes(at.toISOString()));
  if (!valid) throw new PickupError(`${hhmm(at)} isn't available any more. Pick another time.`);
  return { readyAt: at, scheduled: true };
}

/** The earliest collection on offer: as soon as possible today, else the first slot of the first day that has one; null when nothing is open. */
export function earliestPickup(days: PickupDay[]): { choice: PickupChoice; at: string; day: PickupDay } | null {
  const today = days[0];
  if (today?.asapReadyAt) return { choice: { type: 'asap' }, at: today.asapReadyAt, day: today };
  const d = days.find((x) => x.slots.length);
  return d ? { choice: { type: 'slot', at: d.slots[0]! }, at: d.slots[0]!, day: d } : null;
}

/** The choice to keep: the customer's while it can still be had, else the earliest on offer (a slot that has passed rolls forward quietly, Saif 22 Oct). */
export function rollPickupForward(choice: PickupChoice, days: PickupDay[]): PickupChoice {
  if (!days.length || pickupAvailable(choice, days)) return choice;
  return earliestPickup(days)?.choice ?? choice;
}

/** "Ready in ~12 min" / "Collect at 12:45" / "Tomorrow at 12:45"; a choice that can't be had any more shows the earliest collection instead (never a telling-off). */
export function pickupLabel(choice: PickupChoice, now: Date, days: PickupDay[]): string {
  if (choice.type === 'asap') {
    const r = days[0]?.asapReadyAt;
    if (r) return `Ready in ~${Math.max(1, Math.round((new Date(r).getTime() - now.getTime()) / 60000))} min`;
    const e = earliestPickup(days);
    return e ? `Earliest collection ${hhmm(new Date(e.at))}${e.day.label === 'Today' ? '' : e.day.label === 'Tomorrow' ? ' tomorrow' : ` ${e.day.label}`}` : 'Closed now · pick a time';
  }
  const at = new Date(choice.at);
  const day = days.find((d) => d.slots.includes(at.toISOString()));
  const t = hhmm(at);
  if (!day) {
    const e = earliestPickup(days);
    return e ? `Earliest collection ${hhmm(new Date(e.at))}${e.day.label === 'Today' ? '' : e.day.label === 'Tomorrow' ? ' tomorrow' : ` ${e.day.label}`}` : 'Closed now · pick a time';
  }
  if (day.label === 'Today') return `Collect at ${t}`;
  if (day.label === 'Tomorrow') return `Tomorrow at ${t}`;
  return `${day.label} at ${t}`;
}
